import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Countries Service
export const countriesService = {
  async getAll() {
    const { data, error } = await supabase?.from('countries')?.select('*')?.eq('is_active', true)?.order('name_en');
    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(id) {
    const { data, error } = await supabase?.from('countries')?.select('*')?.eq('id', id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Beans Service
export const beansService = {
  async getAll(filters = {}) {
    let query = supabase?.from('beans')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh, is_verified),
        farm:farms(id, name_en, name_zh, centroid_lat, centroid_lng, geojson),
        images:bean_images(id, image_url, alt_text_en, alt_text_zh, display_order, is_primary)
      `)?.eq('is_active', true);

    if (filters?.countryId) {
      query = query?.eq('country_id', filters?.countryId);
    }
    if (filters?.search) {
      query = query?.or(`name_en.ilike.%${filters?.search}%,name_zh.ilike.%${filters?.search}%`);
    }
    if (filters?.minPrice) {
      query = query?.gte('price_usd_per_kg', filters?.minPrice);
    }
    if (filters?.maxPrice) {
      query = query?.lte('price_usd_per_kg', filters?.maxPrice);
    }
    if (filters?.processingType) {
      query = query?.eq('processing_type', filters?.processingType);
    }
    if (filters?.minElevation) {
      query = query?.gte('altitude_m', filters?.minElevation);
    }
    if (filters?.maxElevation) {
      query = query?.lte('altitude_m', filters?.maxElevation);
    }
    if (filters?.isFeatured !== undefined) {
      query = query?.eq('is_featured', filters?.isFeatured);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(id) {
    const { data, error } = await supabase?.from('beans')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh, is_verified, contact_email, contact_phone, website),
        farm:farms(id, name_en, name_zh, region, centroid_lat, centroid_lng, altitude_m, geojson),
        images:bean_images(id, image_url, alt_text_en, alt_text_zh, display_order, is_primary)
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  async getFeatured(countryId = null) {
    let query = supabase?.from('beans')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        images:bean_images(id, image_url, alt_text_en, alt_text_zh, display_order, is_primary)
      `)?.eq('is_featured', true)?.eq('is_active', true);

    if (countryId) {
      query = query?.eq('country_id', countryId);
    }

    const { data, error } = await query?.order('created_at', { ascending: false })?.limit(6);
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Suppliers Service
export const suppliersService = {
  async getAll(filters = {}) {
    let query = supabase?.from('suppliers')?.select(`
        *,
        country:countries(id, name_en, name_zh, code)
      `);

    if (filters?.countryId) {
      query = query?.eq('country_id', filters?.countryId);
    }
    if (filters?.verifiedOnly) {
      query = query?.eq('is_verified', true);
    }
    if (filters?.certifications && filters?.certifications?.length > 0) {
      query = query?.contains('certifications', filters?.certifications);
    }

    const { data, error } = await query?.order('name_en');
    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(id) {
    const { data, error } = await supabase?.from('suppliers')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        farms:farms(id, name_en, name_zh, centroid_lat, centroid_lng)
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Farms Service
export const farmsService = {
  async getAll(filters = {}) {
    let query = supabase?.from('farms')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh, is_verified)
      `);

    if (filters?.countryId) {
      query = query?.eq('country_id', filters?.countryId);
    }
    if (filters?.minAltitude) {
      query = query?.gte('altitude_m', filters?.minAltitude);
    }
    if (filters?.maxAltitude) {
      query = query?.lte('altitude_m', filters?.maxAltitude);
    }

    const { data, error } = await query?.order('name_en');
    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(id) {
    const { data, error } = await supabase?.from('farms')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh, is_verified)
      `)?.eq('id', id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Sponsor Banners Service
export const sponsorBannersService = {
  async getActive() {
    const { data, error } = await supabase?.from('sponsor_banners')?.select('*')?.eq('is_active', true)?.order('display_order');
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Buyer Queries Service
export const buyerQueriesService = {
  async create(queryData) {
    const snakeData = toSnakeCase(queryData);
    const { data, error } = await supabase?.from('buyer_queries')?.insert(snakeData)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async getAll(filters = {}) {
    let query = supabase?.from('buyer_queries')?.select(`
        *,
        bean:beans(id, name_en, name_zh)
      `);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.chineseOnly) {
      query = query?.eq('is_chinese_buyer', true);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    if (error) throw error;
    return toCamelCase(data);
  },

  async updateStatus(id, status, adminNotes = null) {
    const updateData = {
      status,
      responded_at: status === 'responded' ? new Date()?.toISOString() : null
    };
    if (adminNotes) {
      updateData.admin_notes = adminNotes;
    }

    const { data, error } = await supabase?.from('buyer_queries')?.update(updateData)?.eq('id', id)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  }
};

// Reports Service
export const reportsService = {
  async getQueryAnalytics(period = 'month') {
    const { data, error } = await supabase?.from('query_analytics')?.select('*')?.order(`${period}_start`, { ascending: false })?.limit(12);
    if (error) throw error;
    return toCamelCase(data);
  },

  async getDashboardStats() {
    // Get total queries
    const { count: totalQueries } = await supabase?.from('buyer_queries')?.select('*', { count: 'exact', head: true });

    // Get Chinese buyer queries
    const { count: chineseQueries } = await supabase?.from('buyer_queries')?.select('*', { count: 'exact', head: true })?.eq('is_chinese_buyer', true);

    // Get pending queries
    const { count: pendingQueries } = await supabase?.from('buyer_queries')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending');

    // Get active suppliers
    const { count: activeSuppliers } = await supabase?.from('suppliers')?.select('*', { count: 'exact', head: true })?.eq('is_verified', true);

    // Get active beans
    const { count: activeBeans } = await supabase?.from('beans')?.select('*', { count: 'exact', head: true })?.eq('is_active', true);

    return {
      totalQueries: totalQueries || 0,
      chineseQueries: chineseQueries || 0,
      pendingQueries: pendingQueries || 0,
      activeSuppliers: activeSuppliers || 0,
      activeBeans: activeBeans || 0
    };
  },

  async getTopCountriesByInterest() {
    const { data, error } = await supabase?.from('buyer_queries')?.select('country_interest')?.not('country_interest', 'is', null);

    if (error) throw error;

    // Count occurrences
    const counts = {};
    data?.forEach(item => {
      const country = item?.country_interest;
      counts[country] = (counts?.[country] || 0) + 1;
    });

    // Convert to array and sort
    return Object.entries(counts)?.map(([country, count]) => ({ country, count }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 5);
  }
};

// Admin CRUD Services with Enhanced Error Handling
export const adminBeansService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('beans')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh, is_verified),
        farm:farms(id, name_en, name_zh),
        images:bean_images(id, image_url, alt_text_en, alt_text_zh, display_order, is_primary)
      `)?.order('created_at', { ascending: false });
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch beans:', error);
      throw new Error(`Failed to fetch beans: ${error?.message}`);
    }
  },

  async create(beanData) {
    try {
      // Validate required fields
      if (!beanData?.nameEn || !beanData?.nameZh) {
        throw new Error('Bean name (English and Chinese) is required');
      }
      if (!beanData?.countryId) {
        throw new Error('Country is required');
      }
      if (!beanData?.supplierId) {
        throw new Error('Supplier is required');
      }
      if (!beanData?.priceUsdPerKg || beanData?.priceUsdPerKg <= 0) {
        throw new Error('Valid price is required');
      }
      if (!beanData?.availableQtyKg || beanData?.availableQtyKg <= 0) {
        throw new Error('Valid quantity is required');
      }

      const snakeData = toSnakeCase(beanData);
      const { data, error } = await supabase?.from('beans')?.insert(snakeData)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to create bean:', error);
      throw new Error(`Failed to create bean: ${error?.message}`);
    }
  },

  async update(id, beanData) {
    try {
      if (!id) throw new Error('Bean ID is required');

      const snakeData = toSnakeCase(beanData);
      const { data, error } = await supabase?.from('beans')?.update(snakeData)?.eq('id', id)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to update bean:', error);
      throw new Error(`Failed to update bean: ${error?.message}`);
    }
  },

  async delete(id) {
    try {
      if (!id) throw new Error('Bean ID is required');

      // Delete related records first to avoid foreign key constraint errors
      // Note: farms are NOT deleted because beans reference farms (beans.farm_id -> farms.id)
      await supabase?.from('bean_images')?.delete()?.eq('bean_id', id);
      await supabase?.from('test_reports')?.delete()?.eq('bean_id', id);
      await supabase?.from('shipping_logistics')?.delete()?.eq('bean_id', id);

      // Now delete the bean
      const { error } = await supabase?.from('beans')?.delete()?.eq('id', id);

      if (error) {
        console.error('Bean delete error:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete bean:', error);
      throw new Error(`Failed to delete bean: ${error?.message}`);
    }
  }
};

export const adminCountriesService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('countries')?.select('*')?.order('name_en');
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      throw new Error(`Failed to fetch countries: ${error?.message}`);
    }
  },

  async create(countryData) {
    try {
      // Validate required fields
      if (!countryData?.nameEn || !countryData?.nameZh) {
        throw new Error('Country name (English and Chinese) is required');
      }
      if (!countryData?.code) {
        throw new Error('Country code is required');
      }
      if (countryData?.code?.length < 2 || countryData?.code?.length > 3) {
        throw new Error('Country code must be 2-3 characters');
      }

      const snakeData = toSnakeCase(countryData);
      const { data, error } = await supabase?.from('countries')?.insert(snakeData)?.select();
      if (error) {
        if (error?.code === '23505') {
          throw new Error('Country code already exists');
        }
        throw error;
      }
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to create country:', error);
      throw new Error(`Failed to create country: ${error?.message}`);
    }
  },

  async update(id, countryData) {
    try {
      if (!id) throw new Error('Country ID is required');

      const snakeData = toSnakeCase(countryData);
      const { data, error } = await supabase?.from('countries')?.update(snakeData)?.eq('id', id)?.select();
      if (error) {
        if (error?.code === '23505') {
          throw new Error('Country code already exists');
        }
        throw error;
      }
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to update country:', error);
      throw new Error(`Failed to update country: ${error?.message}`);
    }
  },

  async delete(id) {
    try {
      if (!id) throw new Error('Country ID is required');

      const { error } = await supabase?.from('countries')?.delete()?.eq('id', id);
      if (error) {
        if (error?.code === '23503') {
          throw new Error('Cannot delete country: it is being used by beans or suppliers');
        }
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Failed to delete country:', error);
      throw new Error(`Failed to delete country: ${error?.message}`);
    }
  }
};

export const adminSuppliersService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('suppliers')?.select(`
        *,
        country:countries(id, name_en, name_zh, code)
      `)?.order('name_en');
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      throw new Error(`Failed to fetch suppliers: ${error?.message}`);
    }
  },

  async create(supplierData) {
    try {
      // Validate required fields
      if (!supplierData?.nameEn || !supplierData?.nameZh) {
        throw new Error('Supplier name (English and Chinese) is required');
      }
      if (!supplierData?.countryId) {
        throw new Error('Country is required');
      }
      if (!supplierData?.contactEmail) {
        throw new Error('Contact email is required');
      }
      if (!supplierData?.contactPhone) {
        throw new Error('Contact phone is required');
      }

      const snakeData = toSnakeCase(supplierData);
      const { data, error } = await supabase?.from('suppliers')?.insert(snakeData)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to create supplier:', error);
      throw new Error(`Failed to create supplier: ${error?.message}`);
    }
  },

  async update(id, supplierData) {
    try {
      if (!id) throw new Error('Supplier ID is required');

      const snakeData = toSnakeCase(supplierData);
      const { data, error } = await supabase?.from('suppliers')?.update(snakeData)?.eq('id', id)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to update supplier:', error);
      throw new Error(`Failed to update supplier: ${error?.message}`);
    }
  },

  async delete(id) {
    try {
      if (!id) throw new Error('Supplier ID is required');

      const { error } = await supabase?.from('suppliers')?.delete()?.eq('id', id);
      if (error) {
        if (error?.code === '23503') {
          throw new Error('Cannot delete supplier: it has associated beans or farms');
        }
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      throw new Error(`Failed to delete supplier: ${error?.message}`);
    }
  }
};

export const adminFarmsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('farms')?.select(`
        *,
        country:countries(id, name_en, name_zh, code),
        supplier:suppliers(id, name_en, name_zh)
      `)?.order('name_en');
      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch farms:', error);
      throw new Error(`Failed to fetch farms: ${error?.message}`);
    }
  },

  async create(farmData) {
    try {
      if (!farmData?.nameEn || !farmData?.nameZh) {
        throw new Error('Farm name (English and Chinese) is required');
      }
      if (!farmData?.countryId) {
        throw new Error('Country is required');
      }
      if (!farmData?.supplierId) {
        throw new Error('Supplier is required');
      }

      const snakeData = toSnakeCase(farmData);
      const { data, error } = await supabase?.from('farms')?.insert(snakeData)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to create farm:', error);
      throw new Error(`Failed to create farm: ${error?.message}`);
    }
  },

  async update(id, farmData) {
    try {
      if (!id) throw new Error('Farm ID is required');

      const snakeData = toSnakeCase(farmData);
      const { data, error } = await supabase?.from('farms')?.update(snakeData)?.eq('id', id)?.select();
      if (error) throw error;
      return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
    } catch (error) {
      console.error('Failed to update farm:', error);
      throw new Error(`Failed to update farm: ${error?.message}`);
    }
  },

  async delete(id) {
    try {
      if (!id) throw new Error('Farm ID is required');

      const { error } = await supabase?.from('farms')?.delete()?.eq('id', id);
      if (error) {
        if (error?.code === '23503') {
          throw new Error('Cannot delete farm: it has associated beans');
        }
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Failed to delete farm:', error);
      throw new Error(`Failed to delete farm: ${error?.message}`);
    }
  }
};

// Admin Test Reports Service
export const adminTestReportsService = {
  async getAll(beanId = null) {
    let query = supabase?.from('test_reports')?.select(`
      *,
      bean:beans(id, name_en, name_zh)
    `)?.order('created_at', { ascending: false });

    if (beanId) {
      query = query?.eq('bean_id', beanId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(reportData) {
    const snakeData = toSnakeCase(reportData);
    const { data, error } = await supabase?.from('test_reports')?.insert(snakeData)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async update(id, reportData) {
    const snakeData = toSnakeCase(reportData);
    const { data, error } = await supabase?.from('test_reports')?.update(snakeData)?.eq('id', id)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async delete(id) {
    const { error } = await supabase?.from('test_reports')?.delete()?.eq('id', id);
    if (error) throw error;
    return true;
  },

  async uploadReport(file, beanId) {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${beanId}/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

    const { data, error } = await supabase?.storage?.from('test-reports')?.upload(fileName, file);
    if (error) throw error;

    const { data: urlData } = supabase?.storage?.from('test-reports')?.getPublicUrl(fileName);
    return { path: fileName, url: urlData?.publicUrl };
  },

  async deleteReportFile(filePath) {
    const { error } = await supabase?.storage?.from('test-reports')?.remove([filePath]);
    if (error) throw error;
    return true;
  }
};

// Admin Shipping Logistics Service
export const adminShippingLogisticsService = {
  async getAll(beanId = null) {
    let query = supabase?.from('shipping_logistics')?.select(`
      *,
      bean:beans(id, name_en, name_zh),
      supplier:suppliers(id, name_en, name_zh)
    `)?.order('created_at', { ascending: false });

    if (beanId) {
      query = query?.eq('bean_id', beanId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(logisticsData) {
    const snakeData = toSnakeCase(logisticsData);
    const { data, error } = await supabase?.from('shipping_logistics')?.insert(snakeData)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async update(id, logisticsData) {
    const snakeData = toSnakeCase(logisticsData);
    const { data, error } = await supabase?.from('shipping_logistics')?.update(snakeData)?.eq('id', id)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async delete(id) {
    const { error } = await supabase?.from('shipping_logistics')?.delete()?.eq('id', id);
    if (error) throw error;
    return true;
  }
};

// Admin Sponsor Banners Service
export const adminSponsorBannersService = {
  async getAll() {
    const { data, error } = await supabase?.from('sponsor_banners')?.select('*')?.order('display_order');
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(bannerData) {
    const snakeData = toSnakeCase(bannerData);
    const { data, error } = await supabase?.from('sponsor_banners')?.insert(snakeData)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async update(id, bannerData) {
    const snakeData = toSnakeCase(bannerData);
    const { data, error } = await supabase?.from('sponsor_banners')?.update(snakeData)?.eq('id', id)?.select();
    if (error) throw error;
    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async delete(id) {
    const { error } = await supabase?.from('sponsor_banners')?.delete()?.eq('id', id);
    if (error) throw error;
    return true;
  },

  async uploadBannerMedia(file) {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

    const { data, error } = await supabase?.storage?.from('sponsor-banners')?.upload(fileName, file);
    if (error) throw error;

    const { data: urlData } = supabase?.storage?.from('sponsor-banners')?.getPublicUrl(fileName);
    return { path: fileName, url: urlData?.publicUrl };
  },

  async deleteBannerMedia(filePath) {
    const { error } = await supabase?.storage?.from('sponsor-banners')?.remove([filePath]);
    if (error) throw error;
    return true;
  }
};

export default {
  countries: countriesService,
  beans: beansService,
  suppliers: suppliersService,
  farms: farmsService,
  sponsorBanners: sponsorBannersService,
  buyerQueries: buyerQueriesService,
  reports: reportsService
};